// react
import React, { useRef, useState } from 'react'

// components
import ItemInfoCell from '_components/item-info-cell'
import Header from '_components/header'
import MoreInfoDropdown from '_components/more-info-dropdown'
import ListView from '_components/list-view'
import ModalCloneAudience from '_components/custom-audiences/modal-clone-audience'
import ModalMergeAudience from '_components/custom-audiences/modal-merge-audience'
import BatchSidebar, { ACTIONS } from '_components/batch-sidebar'

// lib
import Events from '@st-operations/uikit/Events.js'
import Storage from '@st-operations/uikit/Storage.js'
import bulkUpdate from '_lib/bulkUpdate.js'

// css
import '../../../css/pages/CustomAudiences.styl'

// constants
import legacy_destinations from '_constants/legacy_destinations.js'

// services
import { mergeAudiences } from '_services/audience'

const STATUSES = ['draft', 'approved', 'archived', 'processed', 'deleted']

// CustomAudiences Page
const CustomAudiencesPage = () => {
  const [state, _setState] = useState({
    batch_percentage: 0,
    batch_size: 0,
    selectedAudiences: [],
    error: null,
    show_info: false,
    isModalCloneOpen: false,
    isModalMergeOpen: false,
    remountKey: 0,
  })

  const setState = (newState) => _setState((prevState) => ({ ...prevState, ...newState }))

  const softRefresh = (error) => !error && setState({ remountKey: Math.random() })

  const listViewItemsRef = useRef([])

  const searchLegacy = (qs, next) => {
    qs.kind = 'custom'
    window._api.get('/legacy-audiences/search', qs, next)
  }

  const cloneABCustomAudiences = (audiences) => {
    const ids = Object.keys(audiences)
    startBatchProcess(ids)
    let bulkAction = {
      body: {},
      ids,
      isBrandedAudience: true,
      audiences,
      method: 'post',
      url: `/custom-audiences/load-ab`,
    }
    bulkUpdate(bulkAction, updateBatchProgress, softRefresh)
  }

  const openModalClone = (items) => {
    setState({
      isModalCloneOpen: true,
      selectedAudiences: items,
    })
  }

  const openModalMerge = (items) => {
    setState({
      isModalMergeOpen: true,
      selectedAudiences: items,
    })
  }

  const merge = (payload) => {
    mergeAudiences(payload)
      .then((response) => {
        const id = response?.insertedIds?.[0]
        id && _history.push(`/audience-creator/${id}`)
      })
      .catch((error) => toast.error('Error merging audience.'))
  }

  const startBatchProcess = (ids) => {
    Storage.set('abort_batch', false)
    Events.emit('set_batch_process', true)
    setState({
      batch_percentage: 0,
      batch_size: ids.length ?? ids.size,
    })
  }

  const updateBatchProgress = () => {
    _setState((prevState) => ({
      ...prevState,
      batch_percentage: prevState.batch_percentage + 1 / prevState.batch_size,
    }))
  }

  const handleCloseInfo = () => {
    setState({ show_info: false })
  }

  const handleSelectionChange = ({ action, items }) => {
    const selectedEntities = listViewItemsRef.current.filter(({ _id }) => items.includes(_id))
    Events.emit('ListView-selection', { action, items, entities: selectedEntities })
  }

  const handleBatchSidebarAction = (action, items) => {
    switch (action) {
      case ACTIONS.CLONE:
        return openModalClone(items)
      case ACTIONS.MERGE:
        return openModalMerge(items)
    }
  }

  return (
    <div id="custom-audiences-page" key={state.remountKey}>
      <Header title="AB Custom Audiences" className="header__container" />
      <If condition={state.show_info}>
        <MoreInfoDropdown onClose={handleCloseInfo} />
      </If>
      <If condition={state.error}>
        <p>{state.error.message}</p>
      </If>
      <div className="batch-sidebar__list-view-wrapper">
        <ListView
          showCheckboxes={true}
          onSelectionChange={handleSelectionChange}
          className="audience-builder-list-view st-list-view--has-sidebar"
          batch_percentage={state.batch_percentage}
          batch_size={state.batch_size}
          itemsRef={listViewItemsRef}
          columns={[
            {
              key: 'name',
              name: 'Name',
              width: 500,
              render: (item) => (
                <ItemInfoCell
                  key={`item-${item.index}`}
                  fields={['destination', 'external_id', 'status']}
                  item={item}
                  value={item}
                  allFields
                />
              ),
              sortable: true,
            },
            {
              format: 'metric',
              key: 'actual_size',
              name: 'Size',
            },
            {
              name: 'Created by',
              key: 'created_by.email',
            },
            {
              name: 'Last Updated',
              key: 'updated_at',
              format: 'date',
              sortable: true,
            },
          ]}
          creatable={false}
          entities="ab-custom-audiences"
          filters={[
            { name: 'destination', options: legacy_destinations, all: true },
            { name: 'status', options: STATUSES, all: true },
          ]}
          persistentFiltersOnRefresh
          search={searchLegacy}
          sort_dir={-1}
          sort_key={'updated_at'}
        />
        <BatchSidebar
          onConfirm={handleBatchSidebarAction}
          actions={[ACTIONS.CLONE, ACTIONS.MERGE]}
        />
      </div>

      <ModalCloneAudience
        selectedAudiences={state.selectedAudiences}
        isModalCloneOpen={state.isModalCloneOpen}
        clone={cloneABCustomAudiences}
        closeModal={() => setState({ isModalCloneOpen: false })}
      />

      <ModalMergeAudience
        selectedAudiences={state.selectedAudiences}
        isModalMergeOpen={state.isModalMergeOpen}
        merge={merge}
        closeModal={() => setState({ isModalMergeOpen: false })}
      />
    </div>
  )
}

// export
export default CustomAudiencesPage
